import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarClock, Package, ChevronRight, AlertTriangle, Flame } from "lucide-react";
import api from "@/lib/api";
import RepairListModal, { fmtPromised } from "./RepairListModal";

export const TONES = {
  today: { bar: "bg-[#9B2C1F]", head: "text-[#9B2C1F]", chip: "bg-[#FDEDE7] text-[#9B2C1F] border-[#F2C4BA]", ring: "hover:border-[#9B2C1F]", Icon: Flame },
  d1: { bar: "bg-[#B45309]", head: "text-[#8A6100]", chip: "bg-[#FFF7E0] text-[#8A6100] border-[#F1DC9A]", ring: "hover:border-[#B45309]", Icon: AlertTriangle },
  d2: { bar: "bg-[#6E8B3D]", head: "text-[#3F5222]", chip: "bg-[#EFF3E6] text-[#3F5222] border-[#CFDBB3]", ring: "hover:border-[#6E8B3D]", Icon: CalendarClock },
  d3: { bar: "bg-[#8A9098]", head: "text-[#4B5158]", chip: "bg-[#F5F6F2] text-[#4B5158] border-[#E6E7E1]", ring: "hover:border-[#8A9098]", Icon: CalendarClock },
  material: { bar: "bg-[#4C5FD5]", head: "text-[#2F3C9E]", chip: "bg-[#EEF0FC] text-[#2F3C9E] border-[#C9CFF3]", ring: "hover:border-[#4C5FD5]", Icon: Package },
};

function RepairCard({ it, tone, onOpen, testid }) {
  const overdue = it.days_left != null && it.days_left < 0;
  return (
    <button
      type="button"
      onClick={() => onOpen(it)}
      data-testid={testid}
      className={`w-full text-left ds-card p-3 transition-all duration-150 hover:shadow-md hover:-translate-y-[1px] border ${tone.ring} group`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="font-mono text-[11px] font-semibold bg-[#F5F6F2] border border-[#E6E7E1] text-[#4B5158] px-1.5 py-[1px] rounded-md" data-testid="board-card-code">{it.code}</span>
        {it.promised_date && (
          <span className={`inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-[1px] border rounded-md ${overdue ? "bg-[#9B2C1F] text-white border-[#9B2C1F]" : tone.chip}`} data-testid="board-card-date">
            {overdue && <Flame size={9} />} {overdue ? `Vencida ${fmtPromised(it.promised_date)}` : fmtPromised(it.promised_date)}
          </span>
        )}
      </div>
      <div className="mt-2 text-[13px] font-semibold text-[#0F1210] leading-tight truncate" data-testid="board-card-device">
        {[it.device_type, it.brand, it.model].filter(Boolean).join(" · ") || "—"}
      </div>
      <div className="mt-0.5 text-[12px] text-[#4B5158] truncate" data-testid="board-card-customer">{it.customer_name || "—"}</div>
      {it.section && <div className="mt-1.5 text-[10px] font-mono uppercase tracking-wider text-[#8A9098] truncate">{it.section}</div>}
      <ChevronRight size={12} className="absolute right-2 bottom-2 opacity-0 group-hover:opacity-60 transition-opacity" />
    </button>
  );
}

function Column({ col, onOpen, onTitle }) {
  const tone = TONES[col.key];
  const clickable = col.key === "material";
  return (
    <div className="min-w-[220px] w-[220px] sm:w-[240px] lg:w-auto lg:min-w-0 flex-1 flex flex-col ds-card overflow-hidden" data-testid={`board-col-${col.key}`}>
      <div className={`h-1 ${tone.bar}`} />
      <button
        type="button"
        disabled={!clickable}
        onClick={() => clickable && onTitle(col)}
        data-testid={`board-col-title-${col.key}`}
        className={`px-3 py-2.5 flex items-center justify-between gap-2 text-left ${clickable ? "hover:bg-[#F5F6F2] cursor-pointer" : "cursor-default"}`}
        title={clickable ? "Ver todas las reparaciones en espera de material" : undefined}
      >
        <span className={`inline-flex items-center gap-1.5 font-display font-bold text-[13px] ${tone.head}`}>
          <tone.Icon size={13} /> {col.label}
          {clickable && <ChevronRight size={12} className="opacity-60" />}
        </span>
        <span className={`text-[10.5px] font-mono px-1.5 py-[1px] border rounded-full ${tone.chip}`} data-testid={`board-col-count-${col.key}`}>{col.total}</span>
      </button>
      <div className="px-2 pb-2 space-y-2 flex-1 bg-[#FBFBF8] pt-2 min-h-[120px]">
        {col.items.length === 0 && (
          <div className="text-[11px] text-[#8A9098] text-center py-6" data-testid={`board-col-empty-${col.key}`}>Sin reparaciones</div>
        )}
        {col.items.map((it) => (
          <div key={it.id} className="relative">
            <RepairCard it={it} tone={tone} onOpen={onOpen} testid={`board-card-${it.code}`} />
          </div>
        ))}
        {clickable && col.total > col.items.length && (
          <button type="button" onClick={() => onTitle(col)} data-testid="board-material-more"
            className="w-full text-[11px] font-medium text-[#2F3C9E] hover:underline py-1">
            + {col.total - col.items.length} más · ver todas
          </button>
        )}
      </div>
    </div>
  );
}

/** Iter 164 — Alertas: fecha estimada vencida (ayer o antes), no entregadas. */
function OverdueAlerts({ items, onOpen }) {
  return (
    <section className="ds-card mt-5" data-testid="overdue-alerts">
      <div className="ds-card-h">
        <div className="t inline-flex items-center gap-2">
          <Flame size={14} className="text-[#9B2C1F]" /> Alertas · fecha estimada vencida
          <span className="text-[10.5px] px-2 py-[2px] rounded-full bg-[#FDEDE7] text-[#9B2C1F] font-semibold tracking-normal" data-testid="overdue-alerts-count">{items.length}</span>
        </div>
        <span className="text-[11px] text-[#8A9098]">De más vencida a menos vencida</span>
      </div>
      <div className="p-3 flex flex-wrap gap-2" data-testid="overdue-alerts-list">
        {items.length === 0 && <div className="text-[12px] text-[#8A9098] py-1" data-testid="overdue-alerts-empty">Sin reparaciones vencidas. ¡Todo al día!</div>}
        {items.map((it) => (
          <button key={it.id} type="button" onClick={() => onOpen(it)} data-testid={`overdue-alert-${it.code}`}
            title={`Estimada para el ${fmtPromised(it.promised_date)} · ${-it.days_left} día(s) de retraso`}
            className="inline-flex items-center gap-2 pl-2 pr-3 py-1.5 rounded-lg border border-[#F2C4BA] bg-[#FDEDE7] hover:bg-[#9B2C1F] hover:text-white hover:border-[#9B2C1F] text-[#9B2C1F] transition-colors group">
            <span className="font-mono text-[11px] font-bold">{it.code}</span>
            <span className="text-[12px] font-medium truncate max-w-[180px]">· {it.customer_name || "—"}</span>
            <span className="font-mono text-[10px] opacity-70 group-hover:opacity-90">-{-it.days_left}d</span>
          </button>
        ))}
      </div>
    </section>
  );
}

/** Iter 163 — Tablero kanban de prioridad por Fecha estimada. */
export default function PromisedBoard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [modal, setModal] = useState(null);

  useEffect(() => {
    const load = () => api.get("/dashboard/promised-board").then((r) => setData(r.data)).catch(() => setData({ columns: [] }));
    load();
    const t = setInterval(load, 120_000);
    return () => clearInterval(t);
  }, []);

  const open = (it) => navigate(`/repairs/${it.id}`);

  return (
    <>
    {data && <OverdueAlerts items={data.overdue || []} onOpen={open} />}
    <section className="ds-card mt-5" data-testid="promised-board">
      <div className="ds-card-h">
        <div className="t">
          Prioridad por fecha estimada
          {data?.today_date && (
            <span className="ml-1 text-[10.5px] px-2 py-[2px] rounded-full bg-[#EFF3E6] text-[#3F5222] font-semibold tracking-normal">
              hoy {fmtPromised(data.today_date)}
            </span>
          )}
        </div>
        <span className="text-[11px] text-[#8A9098]">Qué vence hoy, mañana, en 2–3 días y qué está bloqueado por material</span>
      </div>
      <div className="overflow-x-auto p-3" data-testid="promised-board-scroll">
        <div className="flex gap-3 items-stretch min-w-max lg:min-w-0">
          {!data ? (
            <div className="text-xs text-[#8A9098] py-8 px-2">Cargando tablero…</div>
          ) : data.columns.map((col) => (
            <Column key={col.key} col={col} onOpen={open} onTitle={(c) => setModal(c)} />
          ))}
        </div>
      </div>
      {modal && (
        <RepairListModal
          title="Espera de material" icon={Package} tone={TONES.material} testid="board-material-modal"
          subtitle={`${modal.total} reparación(es) bloqueadas por material pendiente. Pulsa una para abrir su ficha.`}
          items={modal.all} onClose={() => setModal(null)} onOpen={(it) => { setModal(null); open(it); }}
        />
      )}
    </section>
    </>
  );
}
